import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  Users, MagnifyingGlass, CaretUp, CaretDown, PushPin, Trash,
  Eye, Check, Warning,
} from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const num = (n) => Number(n || 0).toLocaleString();

const DISPLAY_OPTIONS = [
  { v: "full_name", l: "Full name" },
  { v: "first_last_initial", l: "First + last initial" },
  { v: "nickname", l: "Nickname" },
  { v: "private", l: "Anonymous" },
];

const Th = ({ label, col, sort, dir, onSort, align = "left", className = "" }) => (
  <th className={`p-2 text-${align} ${className}`}>
    <button
      type="button"
      onClick={() => onSort(col)}
      data-testid={`participants-sort-${col}`}
      className={`inline-flex items-center gap-1 uppercase tracking-[0.15em] text-[9px] font-bold ${
        sort === col ? "text-[#FF1B91]" : "text-white/40 hover:text-white/70"
      }`}
    >
      {label}
      {sort === col && (dir === "asc" ? <CaretUp size={9} weight="bold" /> : <CaretDown size={9} weight="bold" />)}
    </button>
  </th>
);

export default function ParticipantsPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("minutes");
  const [dir, setDir] = useState("desc");
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [draftPref, setDraftPref] = useState("full_name");
  const [draftNick, setDraftNick] = useState("");
  const [msg, setMsg] = useState("");

  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/admin/participants`, { headers: { "X-Admin-Token": token } });
      setData(await r.json());
    } catch (e) {
      setData({ error: e?.message });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onSort = (col) => {
    if (col === sort) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(col); setDir(col === "name" ? "asc" : "desc"); }
  };

  const saveDisplay = async (p) => {
    setMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/participants/${p.id}/display`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ public_display: draftPref, public_nickname: draftNick }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setMsg(`${j.name} now shows publicly as “${j.display_name}”`);
      setEditing(null);
      load();
    } catch (e) {
      setMsg(e?.message || "Could not save");
    }
    setTimeout(() => setMsg(""), 7000);
  };

  const patchRow = async (p, body) => {
    await fetch(`${BACKEND}/api/admin/sacrifice/${p.id}`, {
      method: "PATCH",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    load();
  };

  const removeRow = async (p) => {
    if (!window.confirm(`Delete ${p.name}? This cannot be undone.`)) return;
    await fetch(`${BACKEND}/api/admin/sacrifice/${p.id}`, { method: "DELETE", headers });
    load();
  };

  const t = data?.totals || {};
  const rows = useMemo(() => {
    let list = (data?.participants || []).filter((p) => !p.is_owner);
    const needle = q.trim().toLowerCase();
    if (needle) {
      list = list.filter((p) =>
        [p.name, p.display_name, p.email, p.phone, p.moving_for, p.location]
          .join(" ").toLowerCase().includes(needle));
    }
    if (filter === "finished") list = list.filter((p) => p.finished);
    if (filter === "working") list = list.filter((p) => !p.finished && p.minutes > 0);
    if (filter === "zero") list = list.filter((p) => p.minutes === 0);
    if (filter === "anonymous") list = list.filter((p) => p.display_name === "Anonymous");
    const mul = dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sort === "name") return mul * String(a.name).localeCompare(String(b.name));
      if (sort === "today") return mul * (a.minutes_today - b.minutes_today);
      if (sort === "joined") return mul * String(a.created_at || "").localeCompare(String(b.created_at || ""));
      return mul * (a.minutes - b.minutes);
    });
  }, [data, q, sort, dir, filter]);

  const chips = [
    ["all", `All ${num(t.participants)}`],
    ["finished", `911 finishers ${num(t.finished)}`],
    ["working", `Working ${num(t.working)}`],
    ["zero", `Not started ${num(t.yet_to_start)}`],
    ["anonymous", `Anonymous ${num(t.anonymous)}`],
  ];

  return (
    <CollapsibleSection
      id="participants"
      testId="participants-panel"
      title="Participants"
      icon={<Users size={20} weight="bold" className="text-[#FF1B91]" />}
      subtitle={data ? `${num(t.participants)} people · ${num(t.minutes)} minutes logged · search, sort, 911 finishers and how each person appears publicly.` : "Search, sort, 911 finishers and how each person appears publicly."}
      onRefresh={load}
      defaultOpen
    >
      <div className="max-w-5xl space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
            <input
              data-testid="participants-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, phone, moving for…"
              className="bg-black border border-white/15 focus:border-[#FF1B91] outline-none pl-9 pr-3 py-2 text-sm text-white w-[300px] max-w-full"
            />
          </div>
          {chips.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              data-testid={`participants-filter-${k}`}
              className={`text-[10px] uppercase tracking-[0.18em] font-bold px-3 py-2 border transition-colors ${
                filter === k
                  ? "border-[#FF1B91] bg-[#FF1B91]/15 text-white"
                  : "border-white/15 text-white/55 hover:text-white hover:border-white/35"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {t.anonymous > 0 && (
          <div data-testid="participants-anonymous-warning" className="border border-yellow-500/50 bg-yellow-500/[0.06] px-4 py-3 flex items-start gap-3">
            <Warning size={18} weight="bold" className="text-yellow-400 shrink-0 mt-0.5" />
            <div className="text-[12px] text-yellow-100/90 leading-relaxed">
              <strong>{t.anonymous}</strong> participant{t.anonymous === 1 ? "" : "s"} currently display as
              &ldquo;Anonymous&rdquo;. Filter by <em>Anonymous</em> above to see why, and use the
              <span className="text-white"> name</span> button on their row to fix it.
            </div>
          </div>
        )}

        {msg && <div data-testid="participants-msg" className="text-[12px] text-emerald-300">{msg}</div>}

        <div className="border border-white/10">
          <div className="overflow-x-auto max-h-[540px]">
            <table className="w-full text-[11px]">
              <thead className="sticky top-0 bg-[#0F0F0F] z-[1]">
                <tr className="border-b border-white/10">
                  <Th label="Name" col="name" sort={sort} dir={dir} onSort={onSort} />
                  <th className="p-2 text-left uppercase tracking-[0.15em] text-[9px] font-bold text-white/40">Shows as</th>
                  <Th label="Minutes" col="minutes" sort={sort} dir={dir} onSort={onSort} align="right" />
                  <Th label="Today" col="today" sort={sort} dir={dir} onSort={onSort} align="right" />
                  <th className="p-2 text-left uppercase tracking-[0.15em] text-[9px] font-bold text-white/40">Status</th>
                  <th className="p-2 text-left uppercase tracking-[0.15em] text-[9px] font-bold text-white/40">Contact</th>
                  <Th label="Joined" col="joined" sort={sort} dir={dir} onSort={onSort} />
                  <th className="p-2 text-right uppercase tracking-[0.15em] text-[9px] font-bold text-white/40">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <Fragment key={p.id}>
                    <tr data-testid={`participant-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.03] align-top">
                      <td className="p-2 text-white whitespace-nowrap">
                        {p.name || <span className="text-white/30">no name</span>}
                        {p.moving_for && <div className="text-white/40 text-[10px]">for {p.moving_for}</div>}
                      </td>
                      <td className="p-2">
                        <span className={p.display_name === "Anonymous" ? "text-yellow-300" : "text-white/70"}>
                          {p.display_name}
                        </span>
                        {p.display_reason && (
                          <div className="text-white/35 text-[10px]">{p.display_reason}</div>
                        )}
                      </td>
                      <td className="p-2 text-right text-white font-bold tabular-nums">{num(p.minutes)}</td>
                      <td className={`p-2 text-right tabular-nums ${p.minutes_today ? "text-[#FFC72C] font-bold" : "text-white/25"}`}>
                        {p.minutes_today ? `+${num(p.minutes_today)}` : "—"}
                      </td>
                      <td className="p-2 whitespace-nowrap">
                        {p.finished ? (
                          <span data-testid={`participant-finisher-${p.id}`} className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] font-bold text-emerald-300 border border-emerald-500/50 bg-emerald-500/10 px-2 py-1">
                            <Check size={9} weight="bold" /> 911 finisher
                          </span>
                        ) : p.minutes > 0 ? (
                          <span className="text-white/50">{num(p.remaining)} to go</span>
                        ) : (
                          <span className="text-white/30">not started</span>
                        )}
                        {p.hidden_from_leaderboard && (
                          <div className="text-yellow-400/70 text-[10px] mt-1">hidden from board</div>
                        )}
                      </td>
                      <td className="p-2 text-white/45">
                        <div className="truncate max-w-[190px]">{p.email || "—"}</div>
                        <div>{p.phone || "—"}</div>
                      </td>
                      <td className="p-2 text-white/40 whitespace-nowrap">{String(p.created_at || "").slice(0, 10)}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          data-testid={`participant-edit-display-${p.id}`}
                          onClick={() => {
                            setEditing(editing === p.id ? null : p.id);
                            setDraftPref(p.display_pref || "full_name");
                            setDraftNick(p.public_nickname || "");
                          }}
                          className="text-[9px] uppercase tracking-[0.18em] font-bold border border-white/20 hover:border-[#FF1B91] text-white/70 hover:text-white px-2 py-1 mr-1"
                        >
                          <Eye size={10} weight="bold" className="inline mr-1" /> Name
                        </button>
                        <button
                          type="button"
                          data-testid={`participant-pin-${p.id}`}
                          onClick={() => patchRow(p, { pinned_top: !p.pinned })}
                          className={`text-[9px] uppercase tracking-[0.18em] font-bold border px-2 py-1 mr-1 ${p.pinned ? "border-[#FFC72C] text-[#FFC72C]" : "border-white/20 text-white/50 hover:text-white"}`}
                        >
                          <PushPin size={10} weight="bold" />
                        </button>
                        <button
                          type="button"
                          data-testid={`participant-delete-${p.id}`}
                          onClick={() => removeRow(p)}
                          className="text-[9px] font-bold border border-white/20 text-white/40 hover:text-[#FF1B91] hover:border-[#FF1B91] px-2 py-1"
                        >
                          <Trash size={10} weight="bold" />
                        </button>
                      </td>
                    </tr>
                    {editing === p.id && (
                      <tr key={`${p.id}-edit`} className="border-b border-white/10 bg-black/50">
                        <td colSpan={8} className="p-3">
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Show publicly as</span>
                            {DISPLAY_OPTIONS.map((o) => (
                              <label key={o.v} className="inline-flex items-center gap-1.5 text-[11px] text-white/80">
                                <input
                                  type="radio"
                                  name={`display-${p.id}`}
                                  value={o.v}
                                  checked={draftPref === o.v}
                                  onChange={() => setDraftPref(o.v)}
                                  data-testid={`participant-display-${o.v}-${p.id}`}
                                />
                                {o.l}
                              </label>
                            ))}
                            {draftPref === "nickname" && (
                              <input
                                value={draftNick}
                                onChange={(e) => setDraftNick(e.target.value)}
                                placeholder="Nickname"
                                data-testid={`participant-nickname-${p.id}`}
                                className="bg-black border border-white/20 focus:border-[#FF1B91] outline-none px-2 py-1 text-[11px] text-white w-40"
                              />
                            )}
                            <button
                              type="button"
                              data-testid={`participant-save-display-${p.id}`}
                              onClick={() => saveDisplay(p)}
                              className="text-[10px] uppercase tracking-[0.2em] font-bold border border-[#FF1B91] bg-[#FF1B91]/10 hover:bg-[#FF1B91]/25 text-white px-3 py-1.5"
                            >
                              Save
                            </button>
                            <button type="button" onClick={() => setEditing(null)} className="text-[10px] uppercase tracking-[0.2em] text-white/45 hover:text-white">
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={8} className="p-4 text-center text-white/40">No participants match.</td></tr>
                )}
                {loading && !data && (
                  <tr><td colSpan={8} className="p-4 text-center text-white/40">Loading…</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-white/35 text-[11px]">
          Showing {rows.length} of {num(t.participants)} · {num(t.minutes_today)} minutes logged today by {num(t.movers_today)} people.
          Need to change someone&rsquo;s login email or phone? Use <span className="text-white/60">Login repair &amp; audit</span> below.
        </p>
      </div>
    </CollapsibleSection>
  );
}
